import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { getResponsablesByStudent } from '../../api/endpoints/users';
import { Loading } from '../Loading/Loading';
import { StudentSearch } from './StudentSearch';
import { AuthContext } from '../../contexts/Auth/AuthContext';

/**
 * Componente para buscar e selecionar um responsável
 * Primeiro seleciona um aluno, depois busca os responsáveis pela tabela users
 */
export const ResponsableSearch = ({ value, onChange, onStudentChange, error, helperText }) => {
  const [loading, setLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [responsables, setResponsables] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const auth = useContext(AuthContext);
  const unit = auth?.user?.unit || 'PATOS';
  const loadingRef = useRef(false); // Para evitar múltiplas chamadas simultâneas

  // Carregar responsáveis quando um aluno for selecionado
  useEffect(() => {
    const loadResponsables = async () => {
      if (!selectedStudent) {
        setResponsables([]);
        return;
      }

      // Se selectedStudent é string no formato "ID-Nome", extrair o ID
      let studentIdSponte;
      if (typeof selectedStudent === 'string') {
        studentIdSponte = selectedStudent.split('-')[0];
      } else {
        studentIdSponte = selectedStudent.idSponte || selectedStudent.id;
      }

      if (!studentIdSponte) {
        setResponsables([]);
        return;
      }

      // Evitar múltiplas chamadas simultâneas
      if (loadingRef.current) {
        return;
      }

      try {
        loadingRef.current = true;
        setLoading(true);
        const responsablesData = await getResponsablesByStudent(
          studentIdSponte,
          unit
        );
        setResponsables(responsablesData || []);
      } catch (error) {
        console.error('Erro ao carregar responsáveis:', error);
        setResponsables([]);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    };

    loadResponsables();
  }, [selectedStudent, unit]);

  // Filtrar responsáveis baseado no searchValue e remover duplicatas
  const filteredResponsables = responsables
    .filter((resp, index, self) => {
      // Remover duplicatas baseado no idSponte
      return index === self.findIndex((r) => r.idSponte === resp.idSponte);
    })
    .filter((resp) => {
      if (!searchValue || searchValue.trim() === '') {
        return true;
      }
      const search = searchValue.toLowerCase().trim();
      const nameMatch = resp.name?.toLowerCase().includes(search);
      const idMatch = resp.idSponte?.toLowerCase().includes(search);
      return nameMatch || idMatch;
    });

  const handleStudentChange = useCallback((newValue) => {
    setSelectedStudent(newValue);
    // Limpar responsável selecionado quando mudar o aluno
    onChange(null);
    setSearchValue('');

    if (onStudentChange) {
      const studentId =
        typeof newValue === 'string'
          ? newValue.split('-')[0]
          : (newValue?.idSponte || newValue?.id || null);
      onStudentChange(studentId ? String(studentId) : null);
    }
  }, [onChange, onStudentChange]);

  const handleChange = useCallback((event, newValue) => {
    // newValue já é um array quando multiple=true
    onChange(Array.isArray(newValue) ? newValue : (newValue ? [newValue] : []));
  }, [onChange]);

  return (
    <>
      <StudentSearch
        value={selectedStudent}
        onChange={handleStudentChange}
        error={error && !selectedStudent}
        helperText={!selectedStudent ? 'Primeiro selecione um aluno' : helperText}
      />
      
      {selectedStudent && (
        <Autocomplete
          multiple
          options={filteredResponsables}
          getOptionLabel={(option) => {
            if (!option) return '';
            return `${option.name} (${option.idSponte})`;
          }}
          value={Array.isArray(value) ? value : (value ? [value] : [])}
          onChange={handleChange}
          loading={loading}
          disablePortal
          filterOptions={(options) => options} // Desabilitar filtro padrão
          inputValue={searchValue}
          onInputChange={(event, newInputValue) => {
            setSearchValue(newInputValue);
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.idSponte}>
              {`${option.name} (${option.idSponte})`}
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <span
                key={option.idSponte}
                {...getTagProps({ index })}
                style={{
                  margin: '2px',
                  padding: '4px 8px',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                }}
              >
                {option.name}
              </span>
            ))
          }
          renderInput={(params) => {
            // Extrair nome do aluno para exibir
            let studentName = '';
            if (typeof selectedStudent === 'string') {
              const parts = selectedStudent.split('-');
              studentName = parts.slice(1).join('-');
            } else {
              studentName = selectedStudent.name || '';
            }
            
            return (
              <TextField
                {...params}
                label="Buscar Responsável (pode selecionar múltiplos)"
                placeholder="Digite o nome do responsável"
                error={error}
                helperText={helperText || `Responsáveis do aluno ${studentName} - Selecione um ou mais`}
                fullWidth
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <Loading size="small" /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            );
          }}
          noOptionsText="Nenhum responsável encontrado para este aluno"
          isOptionEqualToValue={(option, value) => option?.idSponte === value?.idSponte}
        />
      )}
    </>
  );
};

